/* ============================================================================
   PHONE CLINIC — ADMIN DESIGN TOKENS  (single source of truth)
   ----------------------------------------------------------------------------
   Every admin colour / radius / font lives here. The other style holders import
   from this file so nothing can drift:
     - src/Layout/AdminLayout/index.js        (MUI adminTheme)
     - src/assets/css/admin-theme.css         (CSS-var MIRROR — keep values in sync)
     - src/Pages/admin/shared/txStyles.js     (CARD / COL_HEAD / INPUT / LABEL)
     - src/Components/PCSelect.js             (react-select skin)
     - src/Components/PhoneClinicGrid/*       (grid + collapsible)

   If you change a value here, update the matching `:root` var in admin-theme.css.
   ============================================================================ */

export const COLORS = {
    /* Brand */
    primary:        '#a32f2f',
    primaryStrong:  '#82161b',
    primaryTint:    'rgba(163, 47, 47, 0.10)',   /* hover / active bg / focus ring */
    primaryTintSoft:'rgba(163, 47, 47, 0.06)',   /* row hover */
    onPrimary:      '#ffffff',
    tertiary:       '#754600',                    /* ochre — print / secondary accent */

    /* Surfaces */
    bg:             '#f9f9ff',
    surface:        '#ffffff',
    surfaceAlt:     '#f1f3ff',                     /* table headers / subtle fills */
    border:         '#e5e7eb',
    borderStrong:   '#d1d5db',

    /* Text */
    text:           '#141b2b',
    textDim:        '#585f6c',
    placeholder:    '#94a3b8',
    fieldDisabled:  '#f3f4f6',

    /* Status (low-saturation bg + saturated text) */
    successBg: '#dcfce7', successText: '#15803d',
    warningBg: '#fef9c3', warningText: '#a16207',
    errorBg:   '#fee2e2', errorText:   '#b91c1c',
    infoBg:    '#dbeafe', infoText:    '#1d4ed8',
    neutralBg: '#dce2f7', neutralText: '#464553',
};

export const RADIUS = {
    input: 4,
    card:  8,
    chip:  12,
};

export const FONT = "'Inter', sans-serif";

/* ── Canonical react-select skin: BORDERED WHITE (matches text inputs) ─────────
   Used by BOTH PCSelect (pcSelectStyles) and txStyles (SELECT_STYLES) so every
   dropdown in the admin looks identical to the text fields next to it. */
export const SELECT_STYLES = {
    control: (base, state) => ({
        ...base,
        backgroundColor: state.isDisabled ? COLORS.fieldDisabled : COLORS.surface,
        border: `1px solid ${state.isFocused ? COLORS.primary : COLORS.border}`,
        borderRadius: RADIUS.input,
        minHeight: 42,
        fontSize: 14,
        fontFamily: FONT,
        boxShadow: state.isFocused ? `0 0 0 2px ${COLORS.primaryTint}` : 'none',
        '&:hover': { borderColor: state.isFocused ? COLORS.primary : COLORS.borderStrong },
    }),
    singleValue:       (base) => ({ ...base, color: COLORS.text }),
    placeholder:       (base) => ({ ...base, color: COLORS.placeholder }),
    input:             (base) => ({ ...base, color: COLORS.text }),
    dropdownIndicator: (base) => ({ ...base, color: COLORS.placeholder }),
    clearIndicator:    (base) => ({ ...base, color: COLORS.placeholder }),
    indicatorSeparator:(base) => ({ ...base, backgroundColor: COLORS.border }),
    menu: (base) => ({
        ...base,
        borderRadius: RADIUS.input,
        overflow: 'hidden',
        fontFamily: FONT,
        zIndex: 100000,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 100000 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? COLORS.primaryTint
            : state.isFocused
                ? COLORS.primaryTintSoft
                : 'transparent',
        color: COLORS.text,
        cursor: 'pointer',
    }),
};

/* react-select theme: replaces the default blue/purple with crimson. */
export const SELECT_THEME = (theme) => ({
    ...theme,
    colors: {
        ...theme.colors,
        primary:   COLORS.primary,
        primary25: COLORS.primaryTintSoft,
        primary50: COLORS.primaryTint,
    },
});
